'use client';

import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';

import { client } from '../../../utils/trpc';

/**
 * Most first discoveries in Fact Forge.
 *
 * This is the one arcade leaderboard worth showing publicly. Every other score
 * in the arcade is measured in the browser and posted up, so it is only as
 * honest as the player; a discovery is minted server-side when a pair resolves
 * to an element that has never existed, so it cannot be fabricated by a client.
 */
const ForgeLeaderboard = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['arcade', 'forgeLeaderboard'],
    queryFn: () =>
      unwrapFromResult(client.arcade.forgeLeaderboard.query({ limit: 10 }))
  });

  const { data: recent } = useQuery({
    queryKey: ['arcade', 'forgeRecentDiscoveries'],
    queryFn: () =>
      unwrapFromResult(client.arcade.forgeRecentDiscoveries.query())
  });

  const entries = data?.entries ?? [];

  return (
    <section className="mt-8">
      <h2 className="text-lg font-emphasis text-textPrimary">
        🏆 Most first discoveries
      </h2>
      <p className="mt-1 text-sm text-textSecondary">
        Credited when you fuse a pair into something that has never existed
        before. Awarded by the server, so it is a real count.
      </p>

      {isLoading && (
        <p className="mt-4 text-sm text-textGray">Loading leaderboard…</p>
      )}

      {!isLoading && entries.length === 0 && (
        <p className="mt-4 text-sm text-textGray">
          Nobody has discovered anything new yet. Be first.
        </p>
      )}

      {entries.length > 0 && (
        <ol className="mt-4 flex flex-col gap-1">
          {entries.map((entry) => (
            <li
              key={entry.userId}
              className="flex items-center justify-between rounded-lg px-3 py-2 text-sm odd:bg-background-secondary"
            >
              <span className="flex items-center gap-3">
                <span className="w-6 tabular-nums text-textGray">
                  {entry.rank}
                </span>
                {entry.username ? (
                  <Link
                    href={`/user/${entry.username}`}
                    className="font-emphasis text-textPrimary hover:underline"
                  >
                    {entry.name || `@${entry.username}`}
                  </Link>
                ) : (
                  <span className="text-textSecondary">Unknown player</span>
                )}
              </span>
              <span className="tabular-nums text-textSecondary">
                {entry.discoveries}
              </span>
            </li>
          ))}
        </ol>
      )}

      {data?.me && (
        <p className="mt-3 text-sm text-textGray">
          You are rank {data.me.rank} with {data.me.discoveries}{' '}
          {data.me.discoveries === 1 ? 'discovery' : 'discoveries'}.
        </p>
      )}

      {recent && recent.length > 0 && (
        <>
          <h3 className="mt-6 text-sm font-emphasis text-textPrimary">
            Just discovered
          </h3>
          <ul className="mt-2 flex flex-wrap gap-2">
            {recent.map((item) => (
              <li
                key={`${item.username}-${item.name}`}
                className="rounded-full border border-ds-border-standard bg-background-secondary px-3 py-1.5 text-sm text-textPrimary"
              >
                <span aria-hidden="true">{item.emoji}</span> {item.name}
                <span className="ml-2 text-xs text-textGray">
                  @{item.username}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
};

export default ForgeLeaderboard;
